import Table from '@eduzz/houston-ui/Table';
import Typography from '@eduzz/houston-ui/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import moment from 'moment';
import React, { useCallback } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';
import IconPaymentMethodProps from 'v2/components/Shared/IconPaymentMethod';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import ordersService from 'v2/services/orders';

import type { IOrder } from 'v2/components/Pages/Orders/interfaces';

import useStyle from './styles';

const List = () => {
  const classes = useStyle();

  useObservable(() => ordersService.load(), []);
  const [isLoading] = useObservable(() => ordersService.isLoading(), []);
  const [orders] = useObservable(() => ordersService.getOrders(), []);
  const [pagination] = useObservable(() => ordersService.getPaginationResponse(), []);

  const ordersChecked = (orders || []).filter((o) => o.checked);

  const openQuickView = useCallback((order: IOrder) => ordersService.openQuickview(order).subscribe(), []);

  const onCheckAll = useCallback(() => ordersService.checkAllOrders(), []);

  const onUnCheckAll = useCallback(() => ordersService.unCheckAllOrders(), []);

  const onCheck = useCallback((invoiceId: number) => ordersService.checkOrder(invoiceId), []);

  const onChangeRowsPerPage = (per_page: number) => {
    ordersService.setPagination({ ...pagination, ...{ per_page, current_page: 1 } });
    document.getElementById('orders_table').scrollIntoView();
  };

  const onChangePage = (page: number) => {
    ordersService.setPagination({ ...pagination, ...{ current_page: page } });
    document.getElementById('orders_table').scrollIntoView();
  };

  const currentIndex = pagination ? (pagination.current_page - 1) * pagination.per_page + 1 : 1;

  return (
    <Table id='orders_table' total={pagination?.total_items || 0} loading={isLoading}>
      <Table.Header>
        <Table.Column width={20}>#</Table.Column>
        <PermissionHide action='myeduzz:minhas-entregas:editar'>
          <Table.Column width={45}>
            <Checkbox
              indeterminate={!!ordersChecked?.length && orders?.length !== ordersChecked.length}
              checked={orders?.length === ordersChecked.length && !!orders?.length}
              onClick={orders?.length === ordersChecked.length ? onUnCheckAll : onCheckAll}
            />
          </Table.Column>
        </PermissionHide>
        <Table.Column width={110}>Fatura</Table.Column>
        <Table.Column width={250}>Produto</Table.Column>
        <Table.Column width={170}>Entrega</Table.Column>
        <Table.Column>Cliente</Table.Column>
        <Table.Column width={100}>Data</Table.Column>
        <Table.Column width={150} align='right'>
          Frete
        </Table.Column>
        <Table.Column width={120} align='center'>
          Ganho
        </Table.Column>
        <Table.Column></Table.Column>
      </Table.Header>
      <Table.Body>
        {orders?.map((order, index) => (
          <Table.Row data={order} key={order.invoice_id} index={index}>
            <Table.Cell>
              <Typography size='xxs'>{currentIndex + index}.</Typography>
            </Table.Cell>
            <PermissionHide action='myeduzz:minhas-entregas:editar'>
              <Table.Cell>
                <Checkbox checked={order.checked === true} onClick={() => onCheck(order.invoice_id)} />
              </Table.Cell>
            </PermissionHide>
            <Table.Cell>
              <div className={`${classes.statusCell} ${order?.payment_status?.alias || ''}`}>
                <Typography size='xxs' weight='semibold'>
                  {order.invoice_id}
                </Typography>
                <Typography className={`${classes.status} ${order.payment_status.alias}`} size='xxxs' weight='bold'>
                  {order?.payment_status?.name || ''}
                </Typography>
              </div>
            </Table.Cell>
            <Table.Cell>
              <Typography size='xxs'>{order.product.title}</Typography>
            </Table.Cell>
            <Table.Cell>
              <Typography size='xxs' weight='semibold'>
                {order.status}
              </Typography>
            </Table.Cell>
            <Table.Cell>
              <Typography size='xxs' weight='semibold'>
                <PermissionHide action='myeduzz:minhas-entregas:ver-dados-clientes' fallback={<em>oculto</em>}>
                  {order.customer.name}
                </PermissionHide>
              </Typography>
            </Table.Cell>
            <Table.Cell>
              <Typography size='xxs'>{moment(order.created_at).format('L')}</Typography>
            </Table.Cell>
            <Table.Cell align='right'>
              <PermissionHide action='myeduzz:minhas-entregas:ocultar-valores' inverse fallback={<em>oculto</em>}>
                <Typography weight='semibold'>
                  <FormattedNumber format='BRL' value={order.freight_value} />
                </Typography>
                <Typography size='xxs'>{order.delivery_type_shortened}</Typography>
              </PermissionHide>
            </Table.Cell>
            <Table.Cell align='right'>
              <PermissionHide action='myeduzz:minhas-entregas:ocultar-valores' inverse fallback={<em>oculto</em>}>
                <Typography weight='semibold'>
                  <FormattedNumber format='BRL' value={order.net_income} />
                  <IconPaymentMethodProps cardBrand={order.card_brand} />
                </Typography>
              </PermissionHide>
            </Table.Cell>
            <Table.Cell align='right'>
              <TableActionButton onClick={() => openQuickView(order)} />
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>

      {!isLoading && pagination && (
        <Table.Pagination
          page={pagination?.current_page}
          perPage={pagination?.per_page}
          optionsPerPage='10,20,50,100,200'
          onChangePage={onChangePage}
          onChangePerPage={onChangeRowsPerPage}
        />
      )}
    </Table>
  );
};
export default List;
